import type { PropsWithChildren } from 'react'
import { addScaleCorrector, motion } from 'motion/react'

import styles from './Mask.module.css'

// CSS Variables from the module
export const maskHeight = 'var(--mask-height)'
const maskWidth = 'var(--mask-width)'
const correctedMaskWidth = `calc(${maskWidth} / var(--mask-scale-x, 1))`

// Helper functions for mask gradients
function createLinearMask(direction: 'right' | 'bottom', size: string): string {
  return `linear-gradient(to ${direction}, transparent 0, #000 ${size}, #000 calc(100% - ${size}), transparent)`
}

function createCornerMask(position: string): string {
  return `radial-gradient(at ${position}, ${CORNER_GRADIENT})`
}

const CORNER_GRADIENT = `#000 0, transparent 71%`

const mask = [
  createLinearMask('right', correctedMaskWidth),
  createLinearMask('bottom', maskHeight),
  createCornerMask('bottom right'),
  createCornerMask('bottom left'),
  createCornerMask('top left'),
  createCornerMask('top right'),
].join(',')

const maskSize = [
  `100% calc(100% - ${maskHeight} * 2)`,
  `calc(100% - ${correctedMaskWidth} * 2) 100%`,
  ...Array(4).fill(`${correctedMaskWidth} ${maskHeight}`),
].join(',')

// Add scale correction for proper mask scaling
addScaleCorrector({
  '--mask-scale-x': {
    correct: (_, { treeScale, projectionDelta }) => {
      const scale = projectionDelta?.x?.scale ?? 1
      const tree = treeScale?.x ?? 1
      return scale * tree
    },
  },
})

type MaskStyle = {
  '--mask-scale-x': number
  WebkitMaskImage: string
  WebkitMaskSize: string
  WebkitMaskPosition: string
  WebkitMaskRepeat: 'no-repeat'
}

/**
 * Mask component that creates a vignette effect around its children
 * Used to create smooth transitions between number changes
 */
export function Mask({
  children,
  layoutDependency,
}: PropsWithChildren<{ layoutDependency?: unknown }>): JSX.Element {
  const style: MaskStyle = {
    '--mask-scale-x': 1,
    WebkitMaskImage: mask,
    WebkitMaskSize: maskSize,
    WebkitMaskPosition:
      'center, center, top left, top right, bottom right, bottom left',
    WebkitMaskRepeat: 'no-repeat',
  }

  return (
    <motion.span
      layout
      layoutDependency={layoutDependency}
      aria-hidden={true}
      className={styles.mask}
      style={style}
    >
      {children}
    </motion.span>
  )
}
